import type {
  CompanyResponse,
  DedupeScope,
  ImportListEmailsResponse,
  ListDuplicatesResponse,
  ListResponse,
  ProfileResponse,
} from '@shared/schemas';

export type LoadStatus = 'idle' | 'loading' | 'loaded' | 'failed';
export type MutationKind =
  | 'enrich'
  | 'deactivate'
  | 'restore'
  | 'expert-validation'
  | 'delete'
  | 'import-emails'
  | 'dedupe'
  | 'duplicate';
export type MutationStatus = 'idle' | 'pending' | 'succeeded' | 'failed';

/** Raw response counts a mutation may return; the view composes the snackbar text. */
export interface MutationResult {
  deactivatedCount?: number;
  restoredCount?: number;
  /** Profiles whose expert validation actually changed (#671), and in which direction. */
  expertValidation?: { validated: boolean; updatedCount: number };
  /** The Surf email import's summary (#631), shown in full rather than as a snackbar. */
  emailImport?: ImportListEmailsResponse;
  dedupedCount?: number;
  /** The copy a duplication created (#662): the snackbar links to it. */
  copy?: { id: string; name: string };
}

export interface MutationState {
  status: MutationStatus;
  kind: MutationKind | null;
  result: MutationResult | null;
  error: string | null;
}

export type DedupePreviewStatus = 'idle' | 'loading' | 'ready' | 'failed';

/**
 * The Deduplicate preview (#640): what a removal would take out, shown in the
 * confirmation dialog. The removal itself goes through `mutation` (kind
 * `dedupe`) and reads the scope from here.
 */
export interface DedupeState {
  status: DedupePreviewStatus;
  scope: DedupeScope | null;
  preview: ListDuplicatesResponse | null;
  error: string | null;
}

export type DuplicateDialogStatus = 'closed' | 'loading' | 'open';

/**
 * The Duplicate dialog (#662). It opens once the names of the licence's lists
 * are in (the copy's suggested name skips them), and the copy itself goes
 * through `mutation` (kind `duplicate`).
 */
export interface DuplicateState {
  status: DuplicateDialogStatus;
  takenNames: string[];
}

/**
 * Redux state for the admin List Detail page. The slice owns the loaded data +
 * the load / mutation lifecycles; ephemeral UI (selected ids, active tab, dialog
 * open flags) stays local to the page and is passed as action payloads.
 */
export interface ListDetailState {
  list: ListResponse | null;
  profiles: ProfileResponse[];
  companies: CompanyResponse[];
  removedProfiles: ProfileResponse[];
  commentCount: number;
  loadStatus: LoadStatus;
  mutation: MutationState;
  dedupe: DedupeState;
  duplicate: DuplicateState;
  /** Set once the list is deleted so the page can navigate away. */
  deleted: boolean;
}

export const emptyMutation: MutationState = {
  status: 'idle',
  kind: null,
  result: null,
  error: null,
};

export const emptyDedupe: DedupeState = {
  status: 'idle',
  scope: null,
  preview: null,
  error: null,
};

export const emptyDuplicate: DuplicateState = {
  status: 'closed',
  takenNames: [],
};

export const initialState: ListDetailState = {
  list: null,
  profiles: [],
  companies: [],
  removedProfiles: [],
  commentCount: 0,
  loadStatus: 'idle',
  mutation: emptyMutation,
  dedupe: emptyDedupe,
  duplicate: emptyDuplicate,
  deleted: false,
};
